import type { BatchCounters, WorkOrderListItem } from '../../api/types';

/**
 * Prévisualisation de la faisabilité d'affecter un OF à une fournée — pure et
 * testable. Sert à mettre en avant/griser les fournées quand un OF est
 * sélectionné, et à prévenir avant un dépôt.
 *
 * - incompatible : cycle non autorisé pour le produit → dépôt impossible.
 * - blocking     : Tc dépassés (Option 2) → dépôt à éviter (dur).
 * - warning      : voies de vide dépassées 💧 ou délai client compromis 📅 (mous).
 *
 * La capacité outillage 🔧 n'est pas évaluée (non développée à ce stade).
 */

export type FeasibilityLevel = 'ok' | 'warning' | 'blocking' | 'incompatible';

export interface FeasibilityReason {
  code: string;
  level: 'blocking' | 'warning';
  message: string;
}

export interface Feasibility {
  level: FeasibilityLevel;
  reasons: FeasibilityReason[];
}

export interface FeasibilityBatch {
  cycleCode: string;
  /** Fin d'occupation (t3), pour le respect du délai client. */
  t3: string;
  counters: Pick<BatchCounters, 'tcUsed' | 'tcMax' | 'vacuumUsed' | 'vacuumMax'>;
}

const RANK: Record<FeasibilityLevel, number> = { incompatible: 3, blocking: 2, warning: 1, ok: 0 };

export function evaluateFeasibility(
  of: Pick<WorkOrderListItem, 'quantity' | 'dueDate' | 'allowedCycleCodes' | 'tcPerPart' | 'vacuumPorts' | 'vacuumShareable'>,
  batch: FeasibilityBatch,
  options: { tcFin: boolean },
): Feasibility {
  // Cycle non autorisé : la fournée est simplement hors-jeu pour cet OF.
  if (!of.allowedCycleCodes.includes(batch.cycleCode)) {
    return { level: 'incompatible', reasons: [] };
  }

  const reasons: FeasibilityReason[] = [];

  // Tc (bloquant) — seulement sous l'Option 2 (calcul Tc fin).
  if (options.tcFin && of.tcPerPart != null) {
    const added = of.quantity * of.tcPerPart;
    if (batch.counters.tcUsed + added > batch.counters.tcMax) {
      reasons.push({ code: 'TC_DEPASSES', level: 'blocking', message: `Tc : ${batch.counters.tcUsed}+${added} > ${batch.counters.tcMax}.` });
    }
  }

  // Voies de vide (avertissement, indicatif). Les pièces partageables ne comptent pas.
  if (!of.vacuumShareable && of.vacuumPorts != null) {
    const added = of.quantity * of.vacuumPorts;
    if (batch.counters.vacuumUsed + added > batch.counters.vacuumMax) {
      reasons.push({ code: 'VOIES_DEPASSEES', level: 'warning', message: `Voies : ${batch.counters.vacuumUsed}+${added} > ${batch.counters.vacuumMax} (indicatif).` });
    }
  }

  // Délai client (avertissement) : l'OF est dû avant la fin de la fournée.
  if (of.dueDate != null && new Date(of.dueDate) < new Date(batch.t3)) {
    reasons.push({ code: 'DELAI_COMPROMIS', level: 'warning', message: 'Délai client antérieur à la fin de la fournée.' });
  }

  const level: FeasibilityLevel = reasons.some((r) => r.level === 'blocking') ? 'blocking' : reasons.length > 0 ? 'warning' : 'ok';
  return { level, reasons };
}

/** Combine plusieurs faisabilités (ex. pour un badge global). Renvoie la pire. */
export function worstLevel(levels: FeasibilityLevel[]): FeasibilityLevel {
  return levels.reduce<FeasibilityLevel>((worst, l) => (RANK[l] > RANK[worst] ? l : worst), 'ok');
}
